import {
  describeMonthDay, findSeasonOverlaps, isoToMonthDay, nextOccurrenceIso, parseMonthDay, todayIso, type SeasonBounds,
} from '../../utils/pricingSeasons';

/**
 * SAM-650 — business-level closures and special days: pure form <-> payload logic.
 *
 * Stored inside `business.settings` (no table of its own), validated on save by the CRUD hook
 * with `business_calendar.py` (sam-lambda-functions, PR #457). The rules below mirror that
 * validator so the editor flags what the save would reject:
 *
 *   closures      [{ id, every_year?, start_date, end_date, reason? }]   inclusive; MM-DD when every_year
 *   special_days  [{ id, every_year?, date, windows: [{open, close}], reason? }]
 *
 * - `every_year` is exactly `true` or absent; recurring bounds are `MM-DD` and may cross the year end.
 * - `reason` is `{en, es}` (a plain string is read as the same text in both languages).
 * - Closures cannot overlap (same sweep as pricing seasons, reused from utils/pricingSeasons.ts).
 * - A special day needs >= 1 window, each `open < close` ("24:00" allowed as close), no midnight
 *   crossing, no overlap between its windows; two special days cannot share a date.
 *
 * `every_year` bound conversion and overlap detection reuse the SAM-626 season helpers.
 */

export interface CalendarWindowForm {
  open: string;
  close: string;
}

export interface BusinessClosureForm {
  id: string;
  every_year?: boolean;
  start_date: string;
  end_date: string;
  reason_en: string;
  reason_es: string;
}

export interface SpecialDayForm {
  id: string;
  every_year?: boolean;
  date: string;
  windows: CalendarWindowForm[];
  reason_en: string;
  reason_es: string;
}

export interface CalendarForms {
  closures: BusinessClosureForm[];
  specialDays: SpecialDayForm[];
}

export interface ClosurePayload {
  id: string;
  every_year?: true;
  start_date: string;
  end_date: string;
  reason?: { en?: string; es?: string };
}

export interface SpecialDayPayload {
  id: string;
  every_year?: true;
  date: string;
  windows: CalendarWindowForm[];
  reason?: { en?: string; es?: string };
}

export interface CalendarPayload {
  closures: ClosurePayload[];
  special_days: SpecialDayPayload[];
}

export interface CalendarErrors {
  /** Messages per closure row index. */
  closures: Record<number, string[]>;
  /** Messages per special day row index (day-level problems). */
  specialDays: Record<number, string[]>;
  /** Per special day index -> window index -> message. */
  windows: Record<number, Record<number, string>>;
  /** A message not tied to a row (e.g. an unmapped backend error). */
  general?: string;
  hasErrors: boolean;
}

export const emptyCalendarErrors = (): CalendarErrors => ({ closures: {}, specialDays: {}, windows: {}, hasErrors: false });

// ---------------------------------------------------------------------------
// settings <-> forms
// ---------------------------------------------------------------------------

/** `business.settings` may come back as an object, a JSON string or null. */
export function parseSettings(settings: unknown): Record<string, unknown> {
  if (settings && typeof settings === 'object' && !Array.isArray(settings)) return settings as Record<string, unknown>;
  if (typeof settings === 'string' && settings.trim()) {
    try {
      const parsed = JSON.parse(settings);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

function reasonParts(reason: unknown): { reason_en: string; reason_es: string } {
  if (typeof reason === 'string') return { reason_en: reason, reason_es: reason };
  if (reason && typeof reason === 'object') {
    const r = reason as { en?: unknown; es?: unknown };
    return {
      reason_en: typeof r.en === 'string' ? r.en : '',
      reason_es: typeof r.es === 'string' ? r.es : '',
    };
  }
  return { reason_en: '', reason_es: '' };
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const objects = (v: unknown): Array<Record<string, unknown>> =>
  Array.isArray(v) ? v.filter((x): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x)) : [];

function uniqueIds<T extends { id: string }>(rows: T[], prefix: string): T[] {
  const used = new Set<string>();
  return rows.map((row, i) => {
    let id = row.id.trim() || `${prefix}-${i + 1}`;
    let n = i + 1;
    while (used.has(id)) { n += 1; id = `${prefix}-${n}`; }
    used.add(id);
    return id === row.id ? row : { ...row, id };
  });
}

export function parseCalendar(settings: unknown): CalendarForms {
  const s = parseSettings(settings);
  const closures = objects(s.closures).map((c, i): BusinessClosureForm => ({
    id: str(c.id) || `closure-${i + 1}`,
    ...(c.every_year === true ? { every_year: true } : {}),
    start_date: str(c.start_date),
    end_date: str(c.end_date),
    ...reasonParts(c.reason),
  }));
  const specialDays = objects(s.special_days).map((d, i): SpecialDayForm => ({
    id: str(d.id) || `special-day-${i + 1}`,
    ...(d.every_year === true ? { every_year: true } : {}),
    date: str(d.date),
    windows: objects(d.windows).map((w) => ({ open: str(w.open), close: str(w.close) })),
    ...reasonParts(d.reason),
  }));
  return { closures: uniqueIds(closures, 'closure'), specialDays: uniqueIds(specialDays, 'special-day') };
}

function reasonPayload(en: string, es: string): { reason?: { en?: string; es?: string } } {
  const reason: { en?: string; es?: string } = {};
  if (en.trim()) reason.en = en.trim();
  if (es.trim()) reason.es = es.trim();
  return Object.keys(reason).length ? { reason } : {};
}

export function serializeCalendar(forms: CalendarForms): CalendarPayload {
  return {
    closures: forms.closures.map((c) => ({
      id: c.id,
      ...(c.every_year === true ? { every_year: true as const } : {}),
      start_date: c.start_date,
      end_date: c.end_date,
      ...reasonPayload(c.reason_en, c.reason_es),
    })),
    special_days: forms.specialDays.map((d) => ({
      id: d.id,
      ...(d.every_year === true ? { every_year: true as const } : {}),
      date: d.date,
      windows: d.windows.map((w) => ({ open: w.open, close: w.close })),
      ...reasonPayload(d.reason_en, d.reason_es),
    })),
  };
}

/**
 * Read-modify-write of `business.settings`: every other key (merchant_id, terminal_id, anything
 * the backend adds later) is carried over untouched; only `closures` / `special_days` are
 * replaced. An empty list is written only when the key already existed (so clearing works) —
 * a business that never had a calendar keeps a byte-identical settings object.
 */
export function mergeCalendarIntoSettings(settings: unknown, calendar: CalendarPayload): Record<string, unknown> {
  const base = parseSettings(settings);
  const merged: Record<string, unknown> = { ...base };
  if (calendar.closures.length || 'closures' in base) merged.closures = calendar.closures;
  if (calendar.special_days.length || 'special_days' in base) merged.special_days = calendar.special_days;
  return merged;
}

// ---------------------------------------------------------------------------
// editing helpers
// ---------------------------------------------------------------------------

export function nextCalendarId(existing: Array<{ id: string }>, prefix: 'closure' | 'special-day'): string {
  const used = new Set(existing.map((r) => r.id));
  let n = existing.length + 1;
  while (used.has(`${prefix}-${n}`)) n += 1;
  return `${prefix}-${n}`;
}

/**
 * Flip a closure between fixed dates and every year, converting the bounds the same way the
 * SAM-626 season editor does (ISO -> MM-DD; MM-DD -> next occurrence). Off removes the key.
 */
export function setClosureEveryYear(c: BusinessClosureForm, on: boolean, today: string = todayIso()): BusinessClosureForm {
  const rest = { ...c };
  delete rest.every_year;
  if (on) return { ...rest, every_year: true, start_date: isoToMonthDay(c.start_date), end_date: isoToMonthDay(c.end_date) };
  const start = nextOccurrenceIso(c.start_date, today);
  const end = start ? nextOccurrenceIso(c.end_date, start) : '';
  return { ...rest, start_date: start, end_date: end };
}

export function setSpecialDayEveryYear(d: SpecialDayForm, on: boolean, today: string = todayIso()): SpecialDayForm {
  const rest = { ...d };
  delete rest.every_year;
  if (on) return { ...rest, every_year: true, date: isoToMonthDay(d.date) };
  return { ...rest, date: nextOccurrenceIso(d.date, today) };
}

// ---------------------------------------------------------------------------
// validation (mirrors business_calendar.validate_business_calendar)
// ---------------------------------------------------------------------------

/** A real `YYYY-MM-DD` day (strict: 2026-02-30 is rejected), via the season helper's parser. */
const isIso = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v) && isoToMonthDay(v) !== '';

/** "HH:MM" -> minutes since midnight ("24:00" allowed), else null. */
export function clockMinutes(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec((value || '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h === 24 && min === 0) return 1440;
  if (h < 0 || h > 23 || min > 59) return null;
  return h * 60 + min;
}

const closureName = (c: BusinessClosureForm) => c.reason_en.trim() || c.reason_es.trim() || c.id;
const specialName = (d: SpecialDayForm) => d.reason_en.trim() || d.reason_es.trim() || d.id;

function datesUsable(every: boolean | undefined, start: string, end: string): boolean {
  if (!start || !end) return false;
  if (every) return !!parseMonthDay(start) && !!parseMonthDay(end);
  return isIso(start) && isIso(end) && end >= start;
}

const closureBounds = (c: BusinessClosureForm): SeasonBounds => ({
  id: c.id, every_year: c.every_year === true ? true : undefined, start_date: c.start_date, end_date: c.end_date,
});
const specialBounds = (d: SpecialDayForm): SeasonBounds => ({
  id: d.id, every_year: d.every_year === true ? true : undefined, start_date: d.date, end_date: d.date,
});

export function validateCalendar(forms: CalendarForms): CalendarErrors {
  const errors = emptyCalendarErrors();
  const addClosure = (i: number, msg: string) => {
    const list = (errors.closures[i] ||= []);
    if (!list.includes(msg)) list.push(msg);
  };
  const addSpecial = (i: number, msg: string) => {
    const list = (errors.specialDays[i] ||= []);
    if (!list.includes(msg)) list.push(msg);
  };
  const addWindow = (i: number, w: number, msg: string) => {
    const map = (errors.windows[i] ||= {});
    if (!map[w]) map[w] = msg;
  };

  forms.closures.forEach((c, i) => {
    if (!c.start_date || !c.end_date) {
      addClosure(i, c.every_year ? 'Set a start and an end day' : 'Set a start and an end date');
    } else if (c.every_year) {
      if (!parseMonthDay(c.start_date) || !parseMonthDay(c.end_date)) addClosure(i, 'Pick a valid start and end day');
    } else if (!isIso(c.start_date) || !isIso(c.end_date)) {
      addClosure(i, 'Pick a valid start and end date');
    } else if (c.end_date < c.start_date) {
      addClosure(i, 'End date is before the start date');
    }
  });
  const closureCandidates = forms.closures.map((c, i) => ({ c, i })).filter(({ c }) => datesUsable(c.every_year, c.start_date, c.end_date));
  findSeasonOverlaps(closureCandidates.map(({ c }) => closureBounds(c))).forEach(([a, b]) => {
    const ia = closureCandidates[a].i;
    const ib = closureCandidates[b].i;
    addClosure(ia, `Overlaps another closure (${closureName(forms.closures[ib])})`);
    addClosure(ib, `Overlaps another closure (${closureName(forms.closures[ia])})`);
  });

  forms.specialDays.forEach((d, i) => {
    if (!d.date) addSpecial(i, d.every_year ? 'Pick a day' : 'Pick a date');
    else if (d.every_year ? !parseMonthDay(d.date) : !isIso(d.date)) addSpecial(i, d.every_year ? 'Pick a valid day' : 'Pick a valid date');

    if (!d.windows.length) {
      addSpecial(i, 'Add at least one time window — a day with no hours is a closure');
      return;
    }
    const ranges: Array<[number, number, number]> = [];
    d.windows.forEach((w, wi) => {
      const open = clockMinutes(w.open);
      const close = clockMinutes(w.close);
      if (open === null || close === null || open >= 1440) {
        addWindow(i, wi, 'Set an open and a close time (HH:MM)');
        return;
      }
      if (close <= open) {
        addWindow(i, wi, 'Close must be after open (windows cannot cross midnight)');
        return;
      }
      const clash = ranges.find(([o, c]) => open < c && o < close);
      if (clash) {
        addWindow(i, wi, `Overlaps window ${clash[2] + 1}`);
        return;
      }
      ranges.push([open, close, wi]);
    });
  });
  const specialCandidates = forms.specialDays
    .map((d, i) => ({ d, i }))
    .filter(({ d }) => d.date && (d.every_year ? !!parseMonthDay(d.date) : isIso(d.date)));
  findSeasonOverlaps(specialCandidates.map(({ d }) => specialBounds(d))).forEach(([a, b]) => {
    const ia = specialCandidates[a].i;
    const ib = specialCandidates[b].i;
    addSpecial(ia, `Another special day falls on this date (${specialName(forms.specialDays[ib])})`);
    addSpecial(ib, `Another special day falls on this date (${specialName(forms.specialDays[ia])})`);
  });

  errors.hasErrors = Object.keys(errors.closures).length > 0
    || Object.keys(errors.specialDays).length > 0
    || Object.keys(errors.windows).length > 0;
  return errors;
}

/**
 * Not an error (the backend accepts it): a special day whose date a business closure covers
 * never opens, because a closure wins. Returned per special day index so the row can say so.
 */
export function specialDaysShadowedByClosures(forms: CalendarForms): Record<number, string> {
  const out: Record<number, string> = {};
  const closures = forms.closures.filter((c) => datesUsable(c.every_year, c.start_date, c.end_date));
  forms.specialDays.forEach((d, i) => {
    if (!d.date || !(d.every_year ? parseMonthDay(d.date) : isIso(d.date))) return;
    const hit = closures.find((c) => findSeasonOverlaps([closureBounds(c), specialBounds(d)]).length > 0);
    if (hit) out[i] = `A closure (${closureName(hit)}) covers this date, so the business stays closed`;
  });
  return out;
}

// ---------------------------------------------------------------------------
// backend 400 -> rows
// ---------------------------------------------------------------------------

export const CALENDAR_ERROR_CODES = ['invalid_business_calendar', 'business_closure_overlap'] as const;
export type CalendarErrorCode = typeof CALENDAR_ERROR_CODES[number];

export interface CalendarApiError {
  code: CalendarErrorCode;
  message: string;
  details: Record<string, unknown>;
}

/**
 * Pull the calendar rejection out of an axios error (or its response body). The field is being
 * renamed from `code` to `error_code`: both are read, `error_code` wins. Anything that is not a
 * calendar rejection returns null (the caller falls back to the generic error toast).
 */
export function readCalendarApiError(error: unknown): CalendarApiError | null {
  const e = error as { response?: { status?: number; data?: unknown } } | null;
  const body = (e && typeof e === 'object' && 'response' in e ? e.response?.data : error) as Record<string, unknown> | null;
  if (!body || typeof body !== 'object') return null;
  const code = (typeof body.error_code === 'string' ? body.error_code : typeof body.code === 'string' ? body.code : '') as string;
  if (!(CALENDAR_ERROR_CODES as readonly string[]).includes(code)) return null;
  const message = typeof body.error === 'string' ? body.error : typeof body.message === 'string' ? body.message : 'The calendar was rejected';
  const details = body.details && typeof body.details === 'object' ? body.details as Record<string, unknown> : {};
  return { code: code as CalendarErrorCode, message, details };
}

const asIds = (v: unknown): string[] => (Array.isArray(v) ? v : [v]).filter((x): x is string => typeof x === 'string');

/**
 * Map a backend rejection onto the rows it names. `details` shapes (business_calendar.py):
 *   {closures: [id, id?]} · {special_days: [id, id?]} · {special_day: id, window: n} ·
 *   {special_day: id, windows: [a, b]} · {special_day: id, index} · {field, index} · {field, id}
 */
export function mapCalendarApiError(apiError: CalendarApiError, forms: CalendarForms): CalendarErrors {
  const errors = emptyCalendarErrors();
  const { details, message } = apiError;
  const text = apiError.code === 'business_closure_overlap'
    ? (details.special_days ? 'Another special day falls on this date' : 'Overlaps another closure')
    : message;
  let mapped = false;
  const markClosure = (i: number) => {
    if (i < 0 || i >= forms.closures.length) return;
    (errors.closures[i] ||= []).push(text);
    mapped = true;
  };
  const markSpecial = (i: number, msg = text) => {
    if (i < 0 || i >= forms.specialDays.length) return;
    (errors.specialDays[i] ||= []).push(msg);
    mapped = true;
  };
  const closureIdx = (id: string) => forms.closures.findIndex((c) => c.id === id);
  const specialIdx = (id: string) => forms.specialDays.findIndex((d) => d.id === id);

  asIds(details.closures).forEach((id) => markClosure(closureIdx(id)));
  asIds(details.special_days).forEach((id) => markSpecial(specialIdx(id)));

  if (typeof details.special_day === 'string' || typeof details.index === 'number') {
    let i = typeof details.special_day === 'string' ? specialIdx(details.special_day) : -1;
    if (i < 0 && typeof details.index === 'number' && details.field !== 'closures') i = details.index;
    const windows = typeof details.window === 'number' ? [details.window]
      : Array.isArray(details.windows) ? details.windows.filter((w): w is number => typeof w === 'number') : [];
    if (i >= 0 && i < forms.specialDays.length && windows.length) {
      windows.forEach((w) => { (errors.windows[i] ||= {})[w] = message; });
      mapped = true;
    } else if (details.field === 'closures' && typeof details.index === 'number') {
      markClosure(details.index);
    } else {
      markSpecial(i);
    }
  }
  if (details.field && typeof details.id === 'string') {
    if (details.field === 'closures') markClosure(closureIdx(details.id));
    else markSpecial(specialIdx(details.id));
  }

  if (!mapped) errors.general = message;
  errors.hasErrors = true;
  return errors;
}

// ---------------------------------------------------------------------------
// next upcoming closure / special day (the app preview's notice)
// ---------------------------------------------------------------------------

export interface UpcomingCalendarEvent {
  kind: 'closure' | 'special';
  id: string;
  /** Concrete inclusive ISO days of this occurrence (a recurring row resolved to its next one). */
  start: string;
  end: string;
  reason: string;
  windows: CalendarWindowForm[];
  /** True when today falls inside it. */
  ongoing: boolean;
}

const isoOf = (y: number, m: number, d: number): string | null => {
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCFullYear(y);
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};

/**
 * Occurrences of a row's bounds around `today`: the fixed dates, or for an every-year row its
 * instances starting from last year up to four years ahead (a range may cross the year end; a
 * Feb 29 bound only exists in leap years, so the next one can be up to four years away).
 */
function occurrences(everyYear: boolean | undefined, start: string, end: string, today: string): Array<[string, string]> {
  if (!everyYear) return isIso(start) && isIso(end) && end >= start ? [[start, end]] : [];
  const s = parseMonthDay(start);
  const e = parseMonthDay(end);
  if (!s || !e) return [];
  const year = Number(today.slice(0, 4));
  const crosses = s[0] > e[0] || (s[0] === e[0] && s[1] > e[1]);
  const out: Array<[string, string]> = [];
  for (let y = year - 1; y <= year + 4; y += 1) {
    const a = isoOf(y, s[0], s[1]);
    const b = isoOf(crosses ? y + 1 : y, e[0], e[1]);
    if (a && b) out.push([a, b]);
  }
  return out;
}

/**
 * The closure or special day the app should mention next: the one in progress today, else the
 * earliest that starts after today. Incomplete or invalid rows are ignored. On the same start day
 * a closure wins over a special day (a closure always wins, see specialDaysShadowedByClosures).
 */
export function nextCalendarEvent(forms: CalendarForms, today: string = todayIso()): UpcomingCalendarEvent | null {
  const candidates: UpcomingCalendarEvent[] = [];
  forms.closures.forEach((c) => {
    occurrences(c.every_year, c.start_date, c.end_date, today).forEach(([start, end]) => {
      if (end < today) return;
      candidates.push({
        kind: 'closure', id: c.id, start, end, reason: c.reason_en.trim() || c.reason_es.trim(), windows: [], ongoing: start <= today,
      });
    });
  });
  forms.specialDays.forEach((d) => {
    occurrences(d.every_year, d.date, d.date, today).forEach(([start, end]) => {
      if (end < today) return;
      candidates.push({
        kind: 'special', id: d.id, start, end, reason: d.reason_en.trim() || d.reason_es.trim(),
        windows: d.windows.filter((w) => clockMinutes(w.open) !== null && clockMinutes(w.close) !== null),
        ongoing: start <= today,
      });
    });
  });
  if (!candidates.length) return null;
  const rank = (e: UpcomingCalendarEvent) => (e.ongoing ? today : e.start);
  candidates.sort((a, b) => (rank(a) < rank(b) ? -1 : rank(a) > rank(b) ? 1 : a.kind === b.kind ? 0 : a.kind === 'closure' ? -1 : 1));
  return candidates[0];
}

const shortDay = (iso: string) => describeMonthDay(iso.slice(5)) || iso;

const windowText = (w: CalendarWindowForm) => `${w.open}–${w.close}`;

/**
 * The app preview's line for an event, e.g. "Closed today · Christmas",
 * "Closed Dec 24 – Dec 26 · Christmas", "Special hours Dec 24 · 09:00–14:00 · Christmas Eve".
 */
export function describeCalendarEvent(e: UpcomingCalendarEvent): { title: string; detail: string } {
  const range = e.start === e.end ? shortDay(e.start) : `${shortDay(e.start)} – ${shortDay(e.end)}`;
  if (e.kind === 'closure') {
    const when = e.ongoing ? (e.start === e.end ? 'today' : `until ${shortDay(e.end)}`) : range;
    return { title: `Closed ${when}`, detail: e.reason };
  }
  const hours = e.windows.map(windowText).join(', ');
  return {
    title: `Special hours ${e.ongoing ? 'today' : range}`,
    detail: [hours, e.reason].filter(Boolean).join(' · '),
  };
}
